import classNames from "classnames";
import { forwardRef, type Ref, useImperativeHandle, useRef } from "react";

import { useComponentState } from "../../../utils/customHook.util";
import "./SortableList.css";

export type SortableListActions<T> = {
    updateValues: (initialItems: T[]) => void;
};

type SortableListProps<T> = {
    initialItems: T[];
    renderItem: (item: T) => React.JSX.Element | null;
    onChange: (items: T[]) => void;
    renderDragOver?: (item: T) => React.JSX.Element | null;
    disabled?: boolean;
};

type SortableListState<T> = {
    sortedItems: T[];
    draggedItem: T | null;
    draggedItemIndex: number | null;
    dragOverIndex: number | null;
};

function SortableList<T extends { id: string; name: string }>() {
    return forwardRef(
        (props: SortableListProps<T>, ref: Ref<SortableListActions<T>>) => {
            const {
                initialItems,
                renderItem,
                renderDragOver,
                onChange,
                disabled = false,
            } = props;

            const { state, setState } = useComponentState<SortableListState<T>>({
                sortedItems: initialItems,
                draggedItem: null,
                draggedItemIndex: null,
                dragOverIndex: null,
            });

            const inputRef = useRef<HTMLDivElement | null>(null);
            useImperativeHandle(ref, () => ({
                updateValues: (sortedItems) => {
                    setState({ sortedItems });
                    onChange(sortedItems);
                },
            }));

            function reset(newItems?: T[]) {
                if (newItems) {
                    setState({
                        draggedItem: null,
                        draggedItemIndex: null,
                        dragOverIndex: null,
                        sortedItems: newItems,
                    });
                } else {
                    setState({
                        draggedItem: null,
                        draggedItemIndex: null,
                        dragOverIndex: null,
                    });
                }
            }

            function handleDragStart(
                _event: React.DragEvent<HTMLDivElement>,
                draggedItem: T,
                draggedItemIndex: number,
            ) {
                setState({ draggedItem, draggedItemIndex });
            }

            function handleDrop(
                _event: React.DragEvent<HTMLDivElement>,
                _draggedItem: T,
                index: number,
            ) {
                if (state.draggedItemIndex !== null) {
                    const newList = [...state.sortedItems];
                    const itemDragged = newList.splice(state.draggedItemIndex, 1)[0];
                    newList.splice(index, 0, itemDragged);
                    reset(newList);
                    onChange(newList);
                }
            }

            function handleDragOver(
                event: React.DragEvent<HTMLDivElement>,
                index: number,
            ) {
                event.preventDefault();

                setState({
                    dragOverIndex: index,
                });
            }

            const dragOverFunction = renderDragOver
                ? (it: T) => renderDragOver(it)
                : (_it: T) => <div className="sortable-list__item-dragover-default" />;

            return (
                <div
                    className={classNames("sortable-list", {
                        "sortable-list--disabled": disabled,
                    })}
                    ref={inputRef}
                >
                    {state.sortedItems.map((it, index) => {
                        const shouldRenderDragOver = state.dragOverIndex === index;

                        return (
                            <div
                                className="sortable-list__draggable"
                                key={it.id}
                                draggable={!disabled}
                                onDrop={(event) => {
                                    handleDrop(event, it, index);
                                }}
                                onDragStart={(event) => {
                                    handleDragStart(event, it, index);
                                }}
                                onDragOver={(event) => {
                                    handleDragOver(event, index);
                                }}
                                onDragEnd={() => {
                                    reset();
                                }}
                            >
                                {renderItem(it)}

                                {shouldRenderDragOver && dragOverFunction(it)}
                            </div>
                        );
                    })}
                </div>
            );
        },
    );
}

export default SortableList;
